import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Campaign, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { searchUsers } from 'apis/restaurant-api/merchant-admin/user/api';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { CampaignAction } from 'modules/campaigns/types';
import { getItemDiscountType } from 'modules/campaigns/utils/getItemDiscountType';

import { CampaignsQueryKeys } from '../../../campaignsQueryKeys';
import { useIsCampaignAnalyticsEnabled } from '../../../hooks/useIsCampaignAnalyticsEnabled';
import { TestIds } from '../../../utils/testUtils';
import { CampaignActionsFooter } from '../../CampaignActionsFooter/CampaignActionsFooter';
import CampaignSidebarLayout from '../../CampaignSidebarLayout';
import CopyCampaignIdButton from '../../CopyCampaignIdButton';
import { AgreementsDetails } from '../../ad-campaign/budget-section/agreements-section/AgreementsDetails';
import { BudgetDetails } from '../../budget-section/BudgetDetails';
import { UNLIMITED_TOTAL_BUDGET_SUM } from '../../budget-section/constants';
import { DiscountedItemsDetails } from '../../discounted-items-section/DiscountedItemsDetails';
import { ItemDiscountTypeDetails } from '../../item-discount-type-section/ItemDiscountTypeDetails/ItemDiscountTypeDetails';
import { ItemDiscountPreview } from '../../preview/ItemDiscount/ItemDiscountPreview';
import ItemDiscountVariantText from '../../preview/ItemDiscount/ItemDiscountVariantText';
import { YuhoCampaignBanner } from '../../preview/PromotionBanner/YuhoCampaignBanner';
import RulesDetails from '../../rules-section/RulesDetails';
import { SchedulingDetails } from '../../scheduling-section/SchedulingDetails';

export type ItemDiscountDetailsProps = {
  venueDetails?: { name: Localized; short_description: Localized; listimage: string | null };
  banner?: React.ReactNode;
  campaignTypesAvailableToEdit: CampaignType[];
  campaign: Campaign;
  timezone: string;
  isLoading?: boolean;
  availableActions?: CampaignAction[];
  country: Country;
  menuId: string;
  onEdit(campaignId: string): void;
  onClone(campaignId: string): void;
  onDelete(campaignId: string): void;
  onTerminate(campaignId: string): void;
};

const ItemDiscountDetails = ({
  venueDetails,
  banner,
  campaignTypesAvailableToEdit,
  campaign,
  timezone,
  isLoading = false,
  availableActions,
  country,
  menuId,
  onEdit,
  onClone,
  onDelete,
  onTerminate,
}: ItemDiscountDetailsProps) => {
  const { currency } = useCampaignModuleContext();
  const { data: author, isFetching: isLoadingAuthor } = useQuery(
    CampaignsQueryKeys.GET_AUTHOR(campaign.created_by),
    async () => {
      const { users } = await searchUsers(campaign.created_by);
      return users.find(user => user.id === campaign.created_by);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!campaign.created_by,
    },
  );
  const { isCampaignAnalyticsEnabled } = useIsCampaignAnalyticsEnabled();

  const itemDiscountType = getItemDiscountType(campaign.item_discount);

  const itemIds =
    campaign.item_discount?.items?.item_ids ??
    (campaign.item_discount?.target_item?.item_id ? [campaign.item_discount.target_item.item_id] : []);

  const campaignBanner = !!itemIds.length && !!campaign.item_discount.item_trigger_count && (
    <YuhoCampaignBanner>
      <ItemDiscountVariantText
        menuId={menuId}
        itemIds={itemIds}
        itemTriggerCount={campaign.item_discount.item_trigger_count}
        discountConfig={{
          discountType: itemDiscountType,
          amount: convertMonetaryIntToDecimal(Number(campaign.item_discount.amount), currency),
          percentage: campaign.item_discount.percentage ?? 0,
          freeItemsCount: campaign.item_discount.free_items_count ?? 0,
        }}
      />
    </YuhoCampaignBanner>
  );

  return (
    <CampaignSidebarLayout
      preview={
        <ItemDiscountPreview
          venueDetails={venueDetails}
          itemIds={itemIds}
          menuId={menuId}
          banner={campaignBanner}
          startDate={campaign.start_date}
          endDate={campaign.end_date}
          timezone={timezone}
          totalBudgetSum={
            !campaign.created_by_wolt
              ? convertMonetaryIntToDecimal(campaign.total_budget_sum || UNLIMITED_TOTAL_BUDGET_SUM, currency)
              : undefined
          }
          firstPurchaseOnly={campaign.first_purchase_only}
        />
      }
      banner={campaignBanner}
      isFloatingBannerEnabled
      tooltip="campaigns.sidebar-preview.product.tooltip"
    >
      <>
        {!!banner && banner}
        <DiscountedItemsDetails
          selectedItemIds={itemIds}
          itemTriggerCount={campaign.item_discount.item_trigger_count}
          menuId={menuId}
        />
        <ItemDiscountTypeDetails itemDiscountConfig={campaign.item_discount} />
        {campaign.min_basket_size !== undefined && (
          <RulesDetails
            data-testid={TestIds.DETAILS.RULES.WRAPPER}
            minBasketSize={convertMonetaryIntToDecimal(campaign.min_basket_size ?? 0, campaign.currency)}
            firstPurchaseOnly={campaign.first_purchase_only}
            usageType={campaign.per_user_usage_limit ? 'limited' : 'indefinitely'}
            perUserUsageLimit={campaign.per_user_usage_limit}
          />
        )}

        {!campaign.created_by_wolt && (
          <BudgetDetails
            data-testid={TestIds.DETAILS.BUDGET.WRAPPER}
            budget={campaign.total_budget_sum || UNLIMITED_TOTAL_BUDGET_SUM}
          />
        )}

        <SchedulingDetails
          data-testid={TestIds.DETAILS.SCHEDULING.WRAPPER}
          startDate={campaign.start_date}
          endDate={campaign.end_date}
          timezone={timezone}
          timeframe={campaign.weekly_schedule ? 'specific-times' : 'open-hours'}
          country={country}
        />

        <AgreementsDetails
          data-testid={TestIds.DETAILS.AGREEMENTS.WRAPPER}
          agreements={[
            <FormattedMessage
              id="campaigns.agreements.terms-and-conditions-statement"
              values={{
                link: (
                  <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                    <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                  </a>
                ),
              }}
            />,
          ]}
          isLoading={isLoadingAuthor}
          author={author}
          isCreatedByWolt={campaign.created_by_wolt}
        />

        {!isCampaignAnalyticsEnabled && campaignTypesAvailableToEdit.includes(campaign.campaign_type) && (
          <CampaignActionsFooter
            campaignId={campaign.id}
            isLoading={isLoading}
            availableActions={availableActions}
            onUpdate={onEdit}
            onClone={onClone}
            onDelete={onDelete}
            onTerminate={onTerminate}
          />
        )}

        <CopyCampaignIdButton dataTestId={TestIds.DETAILS.ID.WRAPPER} campaignId={campaign.id} />
      </>
    </CampaignSidebarLayout>
  );
};

export default ItemDiscountDetails;
